import React, { useState } from 'react';
import T from '../../components/common/T';
import { useNavigate } from 'react-router-dom';
import { useAccounting } from '../../context/AccountingContext';
import { useProjects } from '../../context/ProjectContext'; 
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useCurrency } from '../../hooks/useCurrency';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

export default function InvoiceForm() {
    const { createInvoice } = useAccounting();
    const { projects } = useProjects();
    const navigate = useNavigate();
    
    const { formatMoney, activeCurrency } = useCurrency();

    // InvoiceForm is always a NEW record — no id until after save
    const { customData, setCustomData, saveCustomFields } = useCustomFields('accounting', null);

    const [formData, setFormData] = useState({
        client_id: '', 
        project_id: '',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '',
        notes: ''
    });

    const [items, setItems] = useState([
        { description: 'Consulting Services', quantity: 1, unit_price: 0 }
    ]);

    const subtotal = items.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unit_price)), 0);
    const tax = subtotal * 0.18; 
    const total = subtotal + tax;

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.client_id || isNaN(formData.client_id)) {
            alert("Please enter a valid Numeric Client ID");
            return;
        }
        
        // Capture the returned invoice so we can get its ID for custom fields
        const savedInvoice = await createInvoice({
            ...formData,
            client_id: parseInt(formData.client_id), 
            items
        });

        // Save custom fields using the new record's ID
        if (savedInvoice?.data?.id) {
            await saveCustomFields(savedInvoice.data.id);
        }

        navigate('/admin/accounting/invoices');
    };

    return (
        <div className="p-8 bg-gray-50 dark:bg-[#0d1117] min-h-screen font-sans transition-colors">
            <div className="max-w-5xl mx-auto">
                
                <div className="flex items-center gap-4 mb-6">
                    <button type="button" onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white transition">
                        <ArrowLeftIcon className="w-6 h-6" />
                    </button>
                    <h1 className="text-base font-bold text-gray-900 dark:text-white"><T>New Invoice</T></h1>
                </div>

                <form onSubmit={handleSubmit} className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] overflow-hidden transition-colors">
                    
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 border-b border-gray-100 dark:border-[#30363d]">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Client ID (Number Only)</T></label>
                                <input 
                                    type="number" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none focus:border-blue-500" 
                                    placeholder="Enter Client ID (e.g. 1)"
                                    value={formData.client_id}
                                    onChange={e => setFormData({...formData, client_id: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Project</T></label>
                                <select 
                                    className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2.5 bg-white dark:bg-[#21262d] text-gray-900 dark:text-white outline-none"
                                    value={formData.project_id}
                                    onChange={e => setFormData({...formData, project_id: e.target.value})}
                                >
                                    <option value=""><T>Select Project...</T></option>
                                    {projects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Invoice Date</T></label>
                                <input 
                                    type="date" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none" 
                                    value={formData.issue_date}
                                    onChange={e => setFormData({...formData, issue_date: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-1"><T>Due Date</T></label>
                                <input 
                                    type="date" 
                                    className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 outline-none" 
                                    value={formData.due_date}
                                    onChange={e => setFormData({...formData, due_date: e.target.value})}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-8 bg-gray-50 dark:bg-[#161b22] transition-colors">
                        <h3 className="text-base font-bold text-gray-500 dark:text-slate-400 uppercase mb-4"><T>Invoice Items</T></h3>
                        <div className="space-y-3">
                            {items.map((item, index) => (
                                <div key={index} className="flex gap-4 items-center">
                                    <input 
                                        type="text" 
                                        placeholder="Description"
                                        className="flex-1 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-md p-2 outline-none"
                                        value={item.description}
                                        onChange={e => handleItemChange(index, 'description', e.target.value)}
                                        required
                                    />
                                    <input 
                                        type="number" 
                                        placeholder="Qty"
                                        className="w-20 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-md p-2 text-center outline-none"
                                        value={item.quantity}
                                        onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                                        min="1"
                                        required
                                    />
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500 dark:text-slate-400">{activeCurrency?.symbol || '$'}</span>
                                        <input 
                                            type="number" 
                                            placeholder="Price"
                                            className="w-32 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-md py-2 pl-7 pr-2 text-right outline-none"
                                            value={item.unit_price}
                                            onChange={e => handleItemChange(index, 'unit_price', e.target.value)}
                                            min="0"
                                            required
                                        />
                                    </div>
                                    <div className="w-32 text-right font-bold text-gray-700 dark:text-slate-200">
                                        {formatMoney(item.quantity * item.unit_price)}
                                    </div>
                                    <button type="button" onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 transition">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addItem} className="mt-4 flex items-center gap-2 text-sm font-bold text-blue-600 dark:text-blue-400 hover:underline">
                            <PlusIcon className="w-4 h-4" /> Add Line Item
                        </button>
                    </div>

                    <div className="p-8 border-t border-gray-200 dark:border-[#30363d] transition-colors">
                        <div className="flex justify-end">
                            <div className="w-64 space-y-2">
                                <div className="flex justify-between text-gray-600 dark:text-slate-400">
                                    <span><T>Subtotal</T></span>
                                    <span>{formatMoney(subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600 dark:text-slate-400">
                                    <span><T>Tax (18%)</T></span>
                                    <span>{formatMoney(tax)}</span>
                                </div>
                                <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white border-t border-gray-300 dark:border-[#30363d] pt-2">
                                    <span><T>Total</T></span>
                                    <span>{formatMoney(total)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Custom Fields */}
                        <div className="mt-6">
                            <CustomFieldRenderer module="accounting" customData={customData} onChange={setCustomData} />
                        </div>
                        
                        <div className="mt-6 flex justify-end gap-4">
                            <button type="button" onClick={() => navigate(-1)} className="px-6 py-3 text-gray-600 dark:text-slate-300 font-bold hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition">
                                Cancel
                            </button>
                            <button type="submit" className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md transition">
                                Save Invoice
                            </button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
}